<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import V4Button from 'next/button/Button.vue';
import InputNext from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  widget: {
    type: Object,
    default: null,
  },
  reports: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update', 'remove', 'change-report']);

const { t } = useI18n();

const TIME_RANGE_OPTIONS = computed(() => [
  { value: '', label: t('WGPT_CUSTOM_DASHBOARDS.FORM.DEFAULT') },
  {
    value: 'last_7_days',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.LAST_7_DAYS'),
  },
  {
    value: 'last_30_days',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.LAST_30_DAYS'),
  },
  {
    value: 'last_90_days',
    label: t('WGPT_CUSTOM_REPORTS.TIME_RANGES.LAST_90_DAYS'),
  },
]);

const GRANULARITY_OPTIONS = computed(() => [
  { value: '', label: t('WGPT_CUSTOM_DASHBOARDS.FORM.DEFAULT') },
  { value: 'day', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.DAY') },
  { value: 'week', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.WEEK') },
  { value: 'month', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.MONTH') },
  { value: 'year', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.YEAR') },
  { value: 'none', label: t('WGPT_CUSTOM_REPORTS.GROUP_BY_OPTIONS.NONE') },
]);

const VISUALIZATION_OPTIONS = computed(() => [
  { value: '', label: t('WGPT_CUSTOM_DASHBOARDS.FORM.DEFAULT') },
  {
    value: 'bar_chart',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.BAR_CHART'),
  },
  {
    value: 'line_chart',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.LINE_CHART'),
  },
  {
    value: 'pie_chart',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.PIE_CHART'),
  },
  {
    value: 'table',
    label: t('WGPT_CUSTOM_REPORTS.CONFIG_EDITOR.CHART_TYPE_OPTIONS.TABLE'),
  },
]);

const VALUE_MODE_OPTIONS = computed(() => [
  { value: '', label: t('WGPT_CUSTOM_DASHBOARDS.FORM.DEFAULT') },
  {
    value: 'absolute',
    label: t('WGPT_CUSTOM_DASHBOARDS.FORM.VALUE_MODES.ABSOLUTE'),
  },
  {
    value: 'percentage',
    label: t('WGPT_CUSTOM_DASHBOARDS.FORM.VALUE_MODES.PERCENTAGE'),
  },
]);

const selectedReport = computed(() => {
  if (!props.widget?.wgpt_custom_report_id) return null;
  return props.reports.find(
    r => r.id === Number(props.widget.wgpt_custom_report_id)
  );
});

const dimensionOptions = computed(() => {
  const dims = selectedReport.value?.config?.dimensions || [];
  return [
    { value: '', label: t('WGPT_CUSTOM_DASHBOARDS.FORM.DEFAULT') },
    // Explicitly ungrouped — '' means "report default", which may itself group.
    { value: 'none', label: t('WGPT_CUSTOM_REPORTS.VIEWER.SUMMARY') },
    ...dims.map(d => ({
      value: d.property || d.source,
      label: d.label || d.property || d.source,
    })),
  ];
});

const widthOptions = computed(() => [
  {
    value: 'half',
    label: t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDTHS.HALF'),
    icon: 'i-lucide-square',
  },
  {
    value: 'full',
    label: t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDTHS.FULL'),
    icon: 'i-lucide-rectangle-horizontal',
  },
]);

const setField = (field, value) => emit('update', { field, value });
const setOverride = (field, value) =>
  emit('update', { field: `overrides.${field}`, value });
</script>

<template>
  <aside
    class="bg-n-surface-2 rounded-lg border border-n-weak flex flex-col h-full overflow-hidden"
  >
    <header class="px-4 py-3 border-b border-n-weak flex items-center gap-2">
      <Icon icon="i-lucide-sliders-horizontal" class="size-4 text-n-slate-11" />
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.SETTINGS_TITLE') }}
      </h3>
    </header>

    <div
      v-if="!widget"
      class="flex-1 flex flex-col items-center justify-center text-center p-6"
    >
      <div
        class="flex items-center justify-center size-12 rounded-full bg-n-alpha-2 mb-3"
      >
        <Icon
          icon="i-lucide-mouse-pointer-square-dashed"
          class="size-6 text-n-slate-10"
        />
      </div>
      <h4 class="text-sm font-medium text-n-slate-12 mb-1">
        {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.SETTINGS_EMPTY_TITLE') }}
      </h4>
      <p class="text-xs text-n-slate-11 max-w-xs">
        {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.SETTINGS_EMPTY_DESCRIPTION') }}
      </p>
    </div>

    <div v-else class="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
      <div class="flex flex-col gap-2">
        <label class="text-xs font-medium text-n-slate-11">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.REPORT') }}
        </label>
        <div
          class="flex items-center gap-2 p-2 bg-n-alpha-1 rounded-lg border border-n-weak"
        >
          <div
            class="flex items-center justify-center size-8 rounded-md bg-n-brand/10 text-n-brand shrink-0"
          >
            <Icon icon="i-lucide-bar-chart-3" class="size-4" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{
                selectedReport?.name ||
                t('WGPT_CUSTOM_DASHBOARDS.FORM.SELECT_REPORT')
              }}
            </p>
          </div>
          <V4Button
            type="button"
            icon="i-lucide-replace"
            size="xs"
            variant="ghost"
            :title="t('WGPT_CUSTOM_DASHBOARDS.FORM.CHANGE_REPORT')"
            @click="emit('change-report')"
          />
        </div>
      </div>

      <InputNext
        :model-value="widget.title"
        :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGET_TITLE')"
        :placeholder="
          selectedReport?.name ||
          t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDGET_TITLE_PLACEHOLDER')
        "
        @update:model-value="setField('title', $event)"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.WIDTH') }}
        </label>
        <div
          class="grid grid-cols-2 gap-1 p-1 bg-n-alpha-1 border border-n-weak rounded-lg"
        >
          <button
            v-for="opt in widthOptions"
            :key="opt.value"
            type="button"
            class="flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm transition"
            :class="
              widget.width === opt.value
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            @click="setField('width', opt.value)"
          >
            <Icon :icon="opt.icon" class="size-4" />
            {{ opt.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-1 [&_select]:w-full [&>div]:w-full">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.TIME_RANGE') }}
        </label>
        <Select
          :model-value="widget.overrides?.time_range || ''"
          :options="TIME_RANGE_OPTIONS"
          @update:model-value="setOverride('time_range', $event)"
        />
      </div>
      <div class="flex flex-col gap-1 [&_select]:w-full [&>div]:w-full">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.TIME_GRANULARITY') }}
        </label>
        <Select
          :model-value="widget.overrides?.time_granularity || ''"
          :options="GRANULARITY_OPTIONS"
          @update:model-value="setOverride('time_granularity', $event)"
        />
      </div>
      <div class="flex flex-col gap-1 [&_select]:w-full [&>div]:w-full">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.DIMENSION') }}
        </label>
        <Select
          :model-value="widget.overrides?.dimension || ''"
          :options="dimensionOptions"
          :disabled="!widget.wgpt_custom_report_id"
          @update:model-value="setOverride('dimension', $event)"
        />
      </div>
      <div class="flex flex-col gap-1 [&_select]:w-full [&>div]:w-full">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.VISUALIZATION') }}
        </label>
        <Select
          :model-value="widget.overrides?.visualization || ''"
          :options="VISUALIZATION_OPTIONS"
          @update:model-value="setOverride('visualization', $event)"
        />
      </div>
      <div class="flex flex-col gap-1 [&_select]:w-full [&>div]:w-full">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ t('WGPT_CUSTOM_DASHBOARDS.FORM.VALUE_MODE') }}
        </label>
        <Select
          :model-value="widget.overrides?.value_mode || ''"
          :options="VALUE_MODE_OPTIONS"
          @update:model-value="setOverride('value_mode', $event)"
        />
      </div>
    </div>

    <footer v-if="widget" class="p-3 border-t border-n-weak">
      <V4Button
        type="button"
        icon="i-lucide-trash-2"
        color="ruby"
        variant="ghost"
        size="sm"
        class="w-full"
        :label="t('WGPT_CUSTOM_DASHBOARDS.FORM.REMOVE_WIDGET')"
        @click="emit('remove')"
      />
    </footer>
  </aside>
</template>
